<template>
    <div class="form-group" v-bind:class="{ checkbox: isCheckboxOrRadio() }">
        <label v-if="showLabel()">
            <template v-if="isCheckboxOrRadio()">
                <input
                    :disabled="disabled"
                    v-bind:type="getInputType"
                    v-bind:checked="isCheckboxOrRadioChecked()"
                    @change="emitCheckBoxOrRadioValue"
                />

                <span></span>
            </template>

            <div>
                <slot />
            </div>
        </label>

        <div
            class="input-group"
            v-if="!isCheckboxOrRadio()"
            v-bind:class="{
                'with-right-offset': rightIcon || (type == 'password' && !isEmpty(value)),
                'input-small': smallMode,
                'right-offset-active': rightIconActive,
            }"
        >
            <input
                v-if="getInputType !== 'textarea'"
                v-bind:placeholder="placeholder"
                v-bind:disabled="disabled"
                v-bind:readonly="readOnly"
                v-bind:class="{ 'has-error': hasError() }"
                v-bind:type="getInputType"
                v-bind:value="value"
                @input="emit('update:modelValue', $event)"
                @change="emit('change', $event)"
                @keyup.enter="emit('enterPressed', $event)"
            />

            <textarea
                ref="textarea"
                v-if="getInputType === 'textarea'"
                v-bind:placeholder="placeholder"
                v-bind:disabled="disabled"
                v-bind:readonly="readOnly"
                v-bind:class="{ 'has-error': hasError() }"
                v-bind:type="getInputType"
                v-bind:value="value"
                @input="emit('update:modelValue', $event)"
                @change="emit('change', $event)"
                @keydown.enter.exact.prevent
                @keyup.enter.exact="$emit('enter')"
                @keydown.enter.shift.exact="textareaNewLine"
            >
            </textarea>

            <div
                class="right-offset"
                v-if="(type == 'password' && !isEmpty(value)) || (type != 'password' && rightIcon)"
                v-bind:class="{ pointer: type == 'password' || rightIconPointer }"
                @click="type == 'password' ? (passwordToggle = !passwordToggle) : $emit('rightIconClick', $event)"
                v-tooltip="rightIconTooltip"
            >
                <i
                    v-bind:class="type == 'password' ? (passwordToggle ? 'fas fa-eye-slash' : 'fas fa-eye') : rightIcon"
                ></i>
            </div>
        </div>

        <span v-if="hasError() && !errorQuiet" class="error">
            <template v-if="isSimpleError()">
                {{ $t(getCurrentError()[0]) }}
            </template>
            <template v-else>
                <template v-if="isNaN(getChoiceError().count)">
                    {{ $tc(getChoiceError().error, {n: getChoiceError().count}) }}
                </template>
                <template v-else>
                    {{ $tc(getChoiceError().error, getChoiceError().count) }}
                </template>
            </template>
        </span>

        <slot name="bottom" />
    </div>
</template>

<script>
import { isEmpty, isBoolean, uniq, isUndefined } from 'lodash'
import inputs from './../mixins/inputs'

export default {
    mixins: [inputs],
    props: {
        modelValue: {
            required: true,
        },
        type: {
            type: String,
            default: 'text',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        name: {
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        rightIcon: {
            type: [Boolean, String],
            default: false,
        },
        rightIconActive: {
            type: Boolean,
            default: false,
        },
        rightIconPointer: {
            type: Boolean,
            default: false,
        },
        rightIconTooltip: {
            type: [Boolean, String],
            default: false,
        },
        smallMode: {
            type: Boolean,
            default: false,
        },
        beforeEmitCallback: {
            type: Function,
            default: (value) => value,
        },
        autoHeight: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        value: function () {
            if (this.getInputType !== 'textarea') {
                return
            }

            if (!this.autoHeight) {
                return
            }

            this.setAutoHeight()
        },
    },
    data() {
        return {
            passwordToggle: false,
        }
    },
    methods: {
        isEmpty,
        isCheckboxOrRadio() {
            return ['radio', 'checkbox'].indexOf(this.type) !== -1
        },
        isCheckboxOrRadioValueBoolean() {
            if ([0, 1].indexOf(this.value) !== -1) {
                return true
            }

            return isEmpty(this.name) && (isBoolean(this.value) || isUndefined(this.value))
        },
        isCheckboxOrRadioChecked() {
            if (this.isCheckboxOrRadioValueBoolean()) {
                return this.value
            }

            if (this.type == 'radio') {
                return this.value == this.name
            }

            return this.value.includes(this.name)
        },
        emitCheckBoxOrRadioValue(e) {
            let checked = e.target.checked

            this.$emit('change', checked)

            if (this.isCheckboxOrRadioValueBoolean()) {
                return this.$emit('update:modelValue', checked)
            }

            if (this.type == 'radio') {
                return this.$emit('update:modelValue', this.name)
            }

            let array = Array.from(this.value)

            if (checked) {
                array.push(this.name)
            }

            this.$emit('update:modelValue', checked ? uniq(array) : array.filter((item) => item != this.name))
        },
        showLabel() {
            if (this.isCheckboxOrRadio()) {
                return true
            }

            return !isEmpty(this.$slots)
        },
        emit(type, e) {
            if (this.type == 'number') {
                let value = e.target.value.replace(',', '.')

                return this.$emit(type, this.beforeEmitCallback(value))
            }

            this.$emit(type, this.beforeEmitCallback(e.target.value))
        },
        textareaNewLine() {
            this.emit('update:modelValue', {
                target: {
                    value: this.value,
                },
            })
        },
        setAutoHeight() {
            this.$refs.textarea.style.height = 'auto'
            this.$refs.textarea.style.height = `${this.$refs.textarea.scrollHeight}px`
        },
    },
    computed: {
		value() {
			return this.modelValue
		},
        getInputType() {
            if (this.type == 'password') {
                return this.passwordToggle ? 'text' : this.type
            }

            if (this.type == 'number') {
                return 'text'
            }

            return this.type
        },
    },
    mounted() {
        if (this.type == 'textarea' && this.autoHeight) {
            this.setAuthHeight()
        }
    },
}
</script>
